import { useState } from "react";
import {
  NotificationBadge,
  NotificationInbox,
  NotificationPreferences,
} from "@sfc/notifications/react";
import { type InboxItem } from "@sfc/notifications/contracts";
import {
  Button,
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@sfc/ui";
import { trpc } from "@client/trpc";
import { useMutationAction } from "@client/actions/useMutationAction";
import { confirmQuery } from "@client/actions/defineAction";
import { QueryContent } from "@client/components/admin-tools/QueryContent";

type Cursor = { createdAt: string; id: string };

export function NotificationCenter({ viewerId }: { viewerId: string }) {
  const [open, setOpen] = useState(false);
  const count = trpc.getNotificationUnreadCount.useQuery(
    { viewerId },
    { refetchInterval: 30000 },
  );
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger
        render={
          <NotificationBadge count={count.data} unavailable={count.isError} />
        }
      />
      <DialogContent className="flex max-h-[85dvh] min-w-0 flex-col sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Notifications</DialogTitle>
          <DialogDescription>
            Your notifications and delivery preferences.
          </DialogDescription>
        </DialogHeader>
        {open && <NotificationPanel viewerId={viewerId} />}
      </DialogContent>
    </Dialog>
  );
}

function NotificationPanel({ viewerId }: { viewerId: string }) {
  const [tab, setTab] = useState("inbox");
  return (
    <Tabs
      value={tab}
      onValueChange={(value) => setTab(String(value))}
      className="min-h-0 min-w-0"
    >
      <TabsList>
        <TabsTrigger value="inbox">Inbox</TabsTrigger>
        <TabsTrigger value="preferences">Preferences</TabsTrigger>
      </TabsList>
      <div className="min-h-0 overflow-y-auto overscroll-contain">
        <TabsContent value="inbox">
          {tab === "inbox" && <InboxPanel viewerId={viewerId} />}
        </TabsContent>
        <TabsContent value="preferences">
          {tab === "preferences" && <PreferencesPanel viewerId={viewerId} />}
        </TabsContent>
      </div>
    </Tabs>
  );
}

function InboxPanel({ viewerId }: { viewerId: string }) {
  const utils = trpc.useUtils();
  const [filter, setFilter] = useState<"open" | "unread" | "archived">("open");
  const [cursors, setCursors] = useState<(Cursor | undefined)[]>([undefined]);
  const input = { viewerId, list: { filter, cursor: cursors.at(-1) } };
  const query = trpc.getNotificationInbox.useQuery(input, {
    refetchInterval: 15000,
  });
  const mutation = trpc.setNotificationState.useMutation();
  const bulkMutation = trpc.markAllNotificationsRead.useMutation();
  const action = useMutationAction({
    name: "notification-state",
    key: (value: {
      id: string;
      state: { read?: boolean; archived?: boolean };
    }) => `${viewerId}:${value.id}`,
    mutate: async (value) => {
      await mutation.mutateAsync({ viewerId, ...value });
      await utils.getNotificationInbox.invalidate();
    },
    confirmWith: () => [
      confirmQuery(utils.getNotificationInbox, input),
      confirmQuery(utils.getNotificationUnreadCount, { viewerId }),
    ],
    errorLabel: "Update notification",
  });
  const bulk = useMutationAction({
    name: "notification-read-all",
    key: () => viewerId,
    mutate: async (readThrough: string) => {
      await bulkMutation.mutateAsync({ viewerId, readThrough });
      await utils.getNotificationInbox.invalidate();
    },
    confirmWith: () => [
      confirmQuery(utils.getNotificationInbox, input),
      confirmQuery(utils.getNotificationUnreadCount, { viewerId }),
    ],
    errorLabel: "Mark notifications read",
  });

  async function openItem(item: InboxItem) {
    const outcome = await action.run({ id: item.id, state: { read: true } });

    if (outcome.ok && item.href) {
      window.location.assign(item.href);
    }
  }

  return (
    <div className="flex flex-col gap-4 pt-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-1" aria-label="Notification view">
          {(["open", "unread", "archived"] as const).map((value) => (
            <Button
              key={value}
              variant={filter === value ? "secondary" : "ghost"}
              aria-pressed={filter === value}
              className="min-h-11"
              onClick={() => {
                setFilter(value);
                setCursors([undefined]);
              }}
            >
              {value === "open"
                ? "Open"
                : value === "unread"
                  ? "Unread"
                  : "Archived"}
            </Button>
          ))}
        </div>
        <Button
          variant="ghost"
          className="min-h-11"
          disabled={!query.data || bulk.isPending(viewerId)}
          onClick={() => {
            if (query.data) {
              void bulk.run(query.data.readThrough);
            }
          }}
        >
          Mark all read
        </Button>
      </div>
      <QueryContent
        query={query}
        scope="panel"
        loadingMessage="Loading notifications"
        errorMessage="Could not load notifications."
      >
        {(data) => (
          <NotificationInbox
            items={data.items}
            pending={(id) => action.isPending(`${viewerId}:${id}`)}
            onStateChange={(id, state) => void action.run({ id, state })}
            onOpen={(item) => void openItem(item)}
            hasMore={data.nextCursor !== null}
            onLoadMore={() => {
              if (data.nextCursor) {
                setCursors([...cursors, data.nextCursor]);
              }
            }}
          />
        )}
      </QueryContent>
      {cursors.length > 1 && (
        <Button
          variant="outline"
          onClick={() => setCursors(cursors.slice(0, -1))}
        >
          Previous page
        </Button>
      )}
    </div>
  );
}

function PreferencesPanel({ viewerId }: { viewerId: string }) {
  const utils = trpc.useUtils();
  const query = trpc.getNotificationPreferences.useQuery({ viewerId });
  const mutation = trpc.setNotificationPreferences.useMutation();
  const action = useMutationAction({
    name: "notification-preferences",
    key: (input: {
      type: string;
      values: { inApp: boolean; email: boolean };
    }) => `${viewerId}:${input.type}`,
    mutate: (input) => mutation.mutateAsync({ viewerId, ...input }),
    confirmWith: () => [
      confirmQuery(utils.getNotificationPreferences, { viewerId }),
    ],
    errorLabel: "Save preferences",
  });
  return (
    <QueryContent
      query={query}
      scope="panel"
      loadingMessage="Loading notification preferences"
      errorMessage="Could not load preferences."
    >
      {(items) => (
        <NotificationPreferences
          items={items}
          pending={(type) => action.isPending(`${viewerId}:${type}`)}
          onChange={(type, channel, enabled) => {
            const item = items.find((candidate) => candidate.type === type);

            if (item) {
              void action.run({
                type,
                values: { ...item.values, [channel]: enabled },
              });
            }
          }}
        />
      )}
    </QueryContent>
  );
}
